import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import Markdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ApprovalCard, fetchApprovals, type Approval } from "./Approvals.tsx";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { cx, ErrorText, Spinner } from "../components/ui.tsx";
import { FilePreview } from "../components/FilePreview.tsx";
import { readSse } from "../lib/sse.ts";
import { fileUrl, isImage, uploadFile, workspaceRel, WORKSPACE } from "../lib/files.ts";
import { WorkspacePicker, type Picked } from "../components/WorkspacePicker.tsx";

/* ── Types ─────────────────────────────────────────────────────────────── */

interface Attachment {
  path: string;
  name: string;
}
interface Tool {
  name: string;
  preview?: string;
  status: "running" | "done" | "failed";
  startedAt?: number;
}
interface Turn {
  key: string;
  role: "user" | "assistant";
  text: string;
  attachments?: Attachment[];
  context?: Picked[];
  tools?: Tool[];
  streaming?: boolean;
  error?: string;
}

const ATTACH_MARK = "\n\n[Attached files — saved in your workspace]\n";
const FILES_ONLY_NOTE = "(Sent without a message — just the attached files.)";
const CONTEXT_MARK = "\n\n[Use these from my workspace as context — read them before answering]\n";

/** Workspace paths the user pointed at, as the server wrote them into the message. */
function parseContext(block: string): Picked[] {
  return block
    .split("\n")
    .map((l) => /^- (\S+?)(\/)?(?: \(folder.*\))?$/.exec(l.trim()))
    .filter((m): m is RegExpExecArray => !!m && m[1]!.startsWith(WORKSPACE + "/"))
    .map((m) => ({ path: m[1]!.slice(WORKSPACE.length + 1), kind: m[2] ? ("dir" as const) : ("file" as const) }));
}

/** Hermes' stored transcript → display turns (tool messages fold into the reply they belong to). */
function toTurns(messages: { id: string; role: string; content: string | null; toolCalls: string[] }[]): Turn[] {
  const out: Turn[] = [];
  let pendingTools: Tool[] = [];
  for (const m of messages) {
    if (m.role === "user") {
      const full = m.content ?? "";
      const ci = full.indexOf(CONTEXT_MARK);
      const ai = full.indexOf(ATTACH_MARK);
      const context = ci >= 0 ? parseContext(full.slice(ci + CONTEXT_MARK.length, ai > ci ? ai : undefined)) : undefined;
      const raw = ci >= 0 ? full.slice(0, ci) + (ai > ci ? full.slice(ai) : "") : full;
      const i = raw.indexOf(ATTACH_MARK);
      const attachments =
        i >= 0
          ? raw
              .slice(i + ATTACH_MARK.length)
              .split("\n")
              .map((l) => workspaceRel(l.replace(/^- /, "")))
              .filter((p): p is string => !!p)
              .map((path) => ({ path, name: path.split("/").pop() ?? path }))
          : undefined;
      const text = i >= 0 ? raw.slice(0, i) : raw;
      // Chat apps mark file-only messages for the assistant; the files already say it here.
      out.push({ key: m.id, role: "user", text: text === FILES_ONLY_NOTE ? "" : text, attachments, context });
      pendingTools = [];
    } else if (m.role === "assistant") {
      pendingTools.push(...m.toolCalls.map((name) => ({ name, status: "done" as const })));
      if (m.content?.trim()) {
        out.push({ key: m.id, role: "assistant", text: m.content, tools: pendingTools });
        pendingTools = [];
      }
    }
  }
  return out;
}

/** Hermes streams images as inline data URLs; react-markdown drops data: URLs unless allowed. Raster only. */
const mediaUrlTransform = (url: string) => (/^data:image\/(png|jpe?g|gif|webp);base64,/.test(url) ? url : defaultUrlTransform(url));

/**
 * Saved replies keep the agent's `MEDIA:<path>` tags (only the live stream inlines them). Workspace
 * images render inline from the file API; other files become download links.
 */
function withMedia(text: string): string {
  return text.replace(/MEDIA:\s*(\S+)/g, (whole, p: string) => {
    const rel = workspaceRel(p.replace(/[.,;:!?)]+$/, ""));
    if (!rel) return whole;
    const name = rel.split("/").pop() ?? rel;
    return isImage(rel) ? `![${name}](${fileUrl(rel)})` : `[📎 ${name}](${fileUrl(rel, true)})`;
  });
}

const prettyTool = (n: string) =>
  n.replace(/^mcp__composio__COMPOSIO_/, "").replace(/^mcp__\w+__/, "").replace(/_/g, " ").toLowerCase();

const SESSION_PAGE = 40;
const MESSAGE_PAGE = 150;

/* ── Page ──────────────────────────────────────────────────────────────── */

export function ChatPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const canUpload = me.data?.group?.permissions.fileUpload ?? false;

  // Chats and messages both load a page at a time; older pages are fetched on demand.
  const sessions = useInfiniteQuery({
    queryKey: ["chat-sessions"],
    queryFn: ({ pageParam }) => unwrap(api.me.assistant.sessions.$get({ query: { limit: String(SESSION_PAGE), offset: String(pageParam) } })),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.hasMore ? all.length * SESSION_PAGE : undefined),
    refetchInterval: 30_000,
  });
  // After an approval decision (or while one is pending) the assistant continues on its own: keep the chat fresh.
  const [followUntil, setFollowUntil] = useState(0);
  const sessionApprovals = useQuery({
    queryKey: ["approvals", "session", sessionId],
    queryFn: () => fetchApprovals({ sessionId: sessionId! }),
    enabled: !!sessionId,
    refetchInterval: 15_000,
  });
  const waiting = (sessionApprovals.data?.approvals ?? []).filter((a) => a.status === "pending");
  const following = followUntil > Date.now() || waiting.length > 0;
  const history = useInfiniteQuery({
    refetchInterval: following ? 5_000 : false,
    queryKey: ["chat-messages", sessionId],
    queryFn: ({ pageParam }) => unwrap(api.me.assistant.sessions[":id"].messages.$get({ param: { id: sessionId! }, query: { limit: String(MESSAGE_PAGE), offset: String(pageParam) } })),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.hasMore ? all.length * MESSAGE_PAGE : undefined),
    enabled: !!sessionId,
  });
  const sessionList = useMemo(() => {
    const seen = new Set<string>();
    return (sessions.data?.pages ?? []).flatMap((p) => p.sessions).filter((s) => !seen.has(s.id) && !!seen.add(s.id));
  }, [sessions.data]);
  // Pages arrive newest-first; the conversation reads oldest-first.
  const historyMessages = useMemo(() => (history.data ? [...history.data.pages].reverse().flatMap((p) => p.messages) : null), [history.data]);

  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const runId = useRef<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  // The session a stream is writing into; stops history reloads from clobbering live turns.
  const liveSession = useRef<string | null>(null);
  // History fetched before the last stream ended is stale (it predates the reply being saved).
  const streamEndedAt = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      if (!busy) setTurns([]);
      return;
    }
    if (historyMessages && liveSession.current !== sessionId && history.dataUpdatedAt >= streamEndedAt.current) setTurns(toTurns(historyMessages));
  }, [sessionId, historyMessages, history.dataUpdatedAt, busy]);

  const patchLast = (fn: (t: Turn) => Turn) => setTurns((ts) => (ts.length ? [...ts.slice(0, -1), fn(ts[ts.length - 1]!)] : ts));

  const send = useCallback(
    async (text: string, attachments: Attachment[], context: Picked[] = []) => {
      setBusy(true);
      setTurns((ts) => [...ts, { key: `u${Date.now()}`, role: "user", text, attachments, context }, { key: `a${Date.now()}`, role: "assistant", text: "", tools: [], streaming: true }]);
      const ctrl = new AbortController();
      abort.current = ctrl;
      liveSession.current = sessionId ?? "new";
      let activeSid = sessionId;
      try {
        const r = await fetch("/api/me/assistant/chat", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sessionId, text, attachments, context }),
          signal: ctrl.signal,
        });
        if (!r.ok || !r.body) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? `Request failed (${r.status})`);
        const sid = r.headers.get("x-session-id");
        if (sid) activeSid = sid;
        if (sid && sid !== sessionId) {
          liveSession.current = sid;
          navigate(`/chat/${sid}`, { replace: true });
        }
        for await (const { event, data } of readSse(r.body)) {
          if (typeof data.run_id === "string") runId.current = data.run_id;
          if (event === "assistant.delta") patchLast((t) => ({ ...t, text: t.text + String(data.delta ?? "") }));
          else if (event === "tool.started")
            patchLast((t) => ({ ...t, tools: [...(t.tools ?? []), { name: String(data.tool_name), preview: data.preview ? String(data.preview) : undefined, status: "running", startedAt: Date.now() }] }));
          else if (event === "tool.completed" || event === "tool.failed") {
            const status = event === "tool.completed" ? "done" : "failed";
            patchLast((t) => {
              const tools = [...(t.tools ?? [])];
              const i = tools.map((x) => x.name).lastIndexOf(String(data.tool_name));
              if (i >= 0) tools[i] = { ...tools[i]!, status };
              return { ...t, tools };
            });
          } else if (event === "assistant.completed") patchLast((t) => ({ ...t, text: String(data.content ?? t.text) || t.text }));
          else if (event === "run.failed") patchLast((t) => ({ ...t, error: String((data as { error?: unknown }).error ?? "The assistant hit an error.") }));
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") patchLast((t) => ({ ...t, error: (e as Error).message }));
      } finally {
        patchLast((t) => ({ ...t, streaming: false, tools: (t.tools ?? []).map((x) => (x.status === "running" ? { ...x, status: "done" } : x)) }));
        setBusy(false);
        runId.current = null;
        abort.current = null;
        liveSession.current = null;
        streamEndedAt.current = Date.now();
        // Reload the saved transcript (titles, tool calls) now that the turn is stored.
        if (activeSid) void qc.invalidateQueries({ queryKey: ["chat-messages", activeSid] });
        void qc.invalidateQueries({ queryKey: ["chat-sessions"] });
      }
    },
    [sessionId, navigate, qc],
  );

  const stop = async () => {
    const id = runId.current;
    if (id) await fetch(`/api/me/assistant/runs/${encodeURIComponent(id)}/stop`, { method: "POST" }).catch(() => {});
    abort.current?.abort();
  };

  const [showList, setShowList] = useState(false);
  useEffect(() => {
    setShowList(false);
  }, [sessionId]);

  return (
    <div className="relative flex h-full min-h-0">
      <SessionList
        sessions={sessionList}
        loading={sessions.isPending}
        error={sessions.error}
        activeId={sessionId}
        busy={busy}
        open={showList}
        more={sessions.hasNextPage ? { loading: sessions.isFetchingNextPage, load: () => void sessions.fetchNextPage() } : null}
      />
      {showList && <div className="absolute inset-0 z-10 bg-black/20 lg:hidden" onClick={() => setShowList(false)} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-[var(--line)] px-3 py-2 lg:hidden">
          <button onClick={() => setShowList(true)} className="rounded-lg px-2 py-1.5 text-sm font-medium hover:bg-[var(--panel-2)]">☰ Chats</button>
          <button onClick={() => navigate("/chat")} disabled={busy} className="ml-auto rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--panel-2)]">+ New</button>
        </div>
        <Conversation
          waiting={waiting}
          onDecided={() => setFollowUntil(Date.now() + 5 * 60_000)}
          turns={turns}
          loading={!!sessionId && history.isPending && !busy}
          onOpenFile={setPreview}
          onPick={(t) => void send(t, [])}
          earlier={history.hasNextPage && !busy ? { loading: history.isFetchingNextPage, load: () => void history.fetchNextPage() } : null}
        />
        <Composer busy={busy} canUpload={canUpload} onSend={send} onStop={stop} onOpenFile={setPreview} />
      </div>
      <FilePreview path={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

/* ── History sidebar ───────────────────────────────────────────────────── */

type More = { loading: boolean; load: () => void } | null;

function SessionList({ sessions, loading, error, activeId, busy, open, more }: { sessions: { id: string; title: string; source: string; lastActive: number }[]; loading: boolean; error: unknown; activeId?: string; busy: boolean; open: boolean; more: More }) {
  const [filter, setFilter] = useState("");
  const sentinel = useRef<HTMLDivElement>(null);
  // Loads the next page as the bottom of the list scrolls into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !more || more.loading) return;
    const io = new IntersectionObserver((e) => {
      if (e[0]?.isIntersecting) more.load();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [more]);
  const shown = filter ? sessions.filter((s) => s.title.toLowerCase().includes(filter.toLowerCase())) : sessions;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const groups = useMemo(() => {
    const now = Date.now() / 1000;
    const b: [string, typeof sessions][] = [["Today", []], ["This week", []], ["Earlier", []]];
    for (const s of shown) b[now - s.lastActive < 86400 ? 0 : now - s.lastActive < 7 * 86400 ? 1 : 2]![1].push(s);
    return b.filter(([, l]) => l.length);
  }, [shown]);
  const rename = async (id: string, current: string) => {
    const title = prompt("Rename chat", current)?.trim();
    if (!title) return;
    await unwrap(api.me.assistant.sessions[":id"].$patch({ param: { id }, json: { title } })).catch((e: Error) => alert(e.message));
    void qc.invalidateQueries({ queryKey: ["chat-sessions"] });
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this chat? This can't be undone.")) return;
    await unwrap(api.me.assistant.sessions[":id"].$delete({ param: { id } })).catch((e: Error) => alert(e.message));
    void qc.invalidateQueries({ queryKey: ["chat-sessions"] });
    if (id === activeId) navigate("/chat");
  };
  return (
    <aside className={cx("w-64 shrink-0 flex-col border-r border-[var(--line)] bg-[var(--panel)] lg:static lg:flex", open ? "absolute inset-y-0 left-0 z-20 flex shadow-xl" : "hidden")}>
      <div className="p-3">
        <button
          disabled={busy}
          onClick={() => navigate("/chat")}
          className="flex h-10 w-full items-center gap-2 rounded-lg border border-[var(--line)] px-3 text-sm font-medium transition hover:bg-[var(--panel-2)] disabled:opacity-50"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          New chat
        </button>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search chats"
          className="mt-2 h-8 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-xs outline-none focus:border-[var(--color-accent)]"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {loading && <div className="grid place-items-center py-8"><Spinner /></div>}
        <ErrorText error={error} />
        {groups.map(([label, list]) => (
          <div key={label} className="mt-2">
            <div className="px-2 py-1.5 text-xs font-medium text-[var(--muted)]">{label}</div>
            {list.map((s) => (
              <div
                key={s.id}
                className={cx("group flex items-center rounded-lg pr-1 transition", s.id === activeId ? "bg-[var(--panel-2)]" : "hover:bg-[var(--panel-2)]")}
              >
                <button disabled={busy} onClick={() => navigate(`/chat/${s.id}`)} className="min-w-0 flex-1 truncate px-2 py-2 text-left text-sm" title={s.title}>
                  {s.source === "cron" && <span className="mr-1 text-xs text-[var(--muted)]">⏱</span>}
                  {s.title}
                </button>
                <button onClick={() => void rename(s.id, s.title)} className="hidden rounded p-1 text-[var(--muted)] hover:text-[var(--text)] group-hover:block" title="Rename">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
                </button>
                <button onClick={() => void remove(s.id)} className="hidden rounded p-1 text-[var(--muted)] hover:text-red-700 group-hover:block" title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
                </button>
              </div>
            ))}
          </div>
        ))}
        {!loading && !sessions.length && <p className="px-2 py-6 text-center text-xs text-[var(--muted)]">Your chats will appear here.</p>}
        {filter && !shown.length && <p className="px-2 py-6 text-center text-xs text-[var(--muted)]">No loaded chat matches.{more ? " Older chats load as you scroll." : ""}</p>}
        {more && (
          <div ref={sentinel} className="py-3 text-center">
            <button onClick={more.load} disabled={more.loading} className="text-xs text-[var(--muted)] hover:text-[var(--text)]">{more.loading ? "Loading…" : "Load older chats"}</button>
          </div>
        )}
      </div>
    </aside>
  );
}

/* ── Conversation ──────────────────────────────────────────────────────── */

const SUGGESTIONS = [
  "What can you help me with?",
  "Summarise the files in my workspace",
  "Draft a weekly status update for my team",
  "Set up a daily 9am briefing of my GitHub notifications",
];

function Conversation({
  turns,
  loading,
  onOpenFile,
  onPick,
  earlier,
  waiting = [],
  onDecided,
}: {
  turns: Turn[];
  loading: boolean;
  onOpenFile: (p: string) => void;
  onPick: (text: string) => void;
  earlier: More;
  waiting?: Approval[];
  onDecided?: () => void;
}) {
  const end = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const prev = useRef<{ first?: string; last?: string; height: number }>({ height: 0 });
  const me = useMe();
  // Block body on purpose: Chrome 152+ returns a Promise from scrollIntoView, and an arrow
  // that returns it would hand React a "cleanup" that isn't a function (crashes on update).
  useEffect(() => {
    const el = scroller.current;
    const first = turns[0]?.key;
    const last = turns[turns.length - 1]?.key;
    // Older messages were added on top: keep the reader where they were instead of jumping to the end.
    if (el && prev.current.last === last && prev.current.first !== first && prev.current.height) {
      el.scrollTop += el.scrollHeight - prev.current.height;
    } else {
      void end.current?.scrollIntoView({ block: "end" });
    }
    prev.current = { first, last, height: el?.scrollHeight ?? 0 };
  }, [turns]);

  if (loading) return <div className="grid flex-1 place-items-center"><Spinner /></div>;
  if (!turns.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Hi {me.data?.user.name.split(" ")[0]}, what can I do for you?</h1>
        <p className="mt-2 text-sm text-[var(--muted)]">Your assistant can use your files, connected apps and the web.</p>
        <div className="mt-8 grid w-full max-w-2xl gap-2 sm:grid-cols-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => onPick(s)} className="rounded-xl border border-[var(--line)] bg-[var(--panel)] px-4 py-3 text-left text-sm transition hover:bg-[var(--panel-2)]">
              {s}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
        {earlier && (
          <div className="text-center">
            <button onClick={earlier.load} disabled={earlier.loading} className="rounded-full border border-[var(--line)] px-3 py-1 text-xs text-[var(--muted)] hover:bg-[var(--panel-2)]">
              {earlier.loading ? "Loading…" : "Load earlier messages"}
            </button>
          </div>
        )}
        {turns.map((t) =>
          t.role === "user" && KRUT_NOTE.test(t.text) ? (
            <KrutNote key={t.key} text={t.text} />
          ) : t.role === "user" ? (
            <div key={t.key} className="flex flex-col items-end gap-2">
              {!!t.context?.length && <ContextChips items={t.context} />}
              {!!t.attachments?.length && <AttachmentStrip items={t.attachments} onOpen={onOpenFile} />}
              {t.text.trim() && <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[var(--panel-2)] px-4 py-2.5 text-[0.925rem]">{t.text}</div>}
            </div>
          ) : (
            <div key={t.key} className="flex gap-3">
              <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--chrome)]">
                <img src="/favicon.png" alt="" className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                {!!t.tools?.length && <ToolTrail tools={t.tools} />}
                {t.text ? (
                  <div className="prose-krut">
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      urlTransform={mediaUrlTransform}
                      components={{
                        code: ({ children, className }) => <CodeOrFile className={className} onOpen={onOpenFile}>{children}</CodeOrFile>,
                        a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer noopener">{children}</a>,
                        img: ({ src, alt }) => <img src={typeof src === "string" ? src : undefined} alt={alt ?? ""} className="my-2 max-h-96 rounded-lg border border-[var(--line)]" />,
                      }}
                    >
                      {withMedia(t.text)}
                    </Markdown>
                  </div>
                ) : t.streaming && !t.tools?.some((x) => x.status === "running") ? (
                  <div className="flex gap-1 py-2">
                    {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-[var(--muted)]" style={{ animationDelay: `${i * 120}ms` }} />)}
                  </div>
                ) : null}
                {t.streaming && t.text && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-[var(--muted)] align-middle" />}
                {t.error && <p className="mt-2 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-700">{t.error}</p>}
              </div>
            </div>
          ),
        )}
        {waiting.map((a) => (
          <div key={a.id} className="ml-10">
            <ApprovalCard a={a} compact onDecided={onDecided} />
          </div>
        ))}
        <div ref={end} />
      </div>
    </div>
  );
}

/** Messages Krut itself puts into a chat (colleague requests and answers, approval decisions). */
const KRUT_NOTE = /^\[(Colleague request #|Answer to your request #|Request #\d+ to |Approval #)/;

function KrutNote({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [head, ...rest] = text.split("\n");
  return (
    <div className="mx-auto w-full max-w-[85%] rounded-xl border border-dashed border-[var(--line)] px-4 py-2 text-xs text-[var(--muted)]">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left">
        <span>🤝</span>
        <span className="min-w-0 flex-1 truncate font-medium">{head?.replace(/^\[|\]$/g, "")}</span>
        {rest.join("").trim() && <span>{open ? "▲" : "▼"}</span>}
      </button>
      {open && <div className="mt-2 whitespace-pre-wrap text-[var(--text)]">{rest.join("\n").trim()}</div>}
    </div>
  );
}

/** Inline code that is a workspace path becomes a clickable file; everything else stays code. */
function CodeOrFile({ children, className, onOpen }: { children: ReactNode; className?: string; onOpen: (p: string) => void }) {
  const text = String(children ?? "");
  const rel = !className && !text.includes("\n") ? workspaceRel(text) : null;
  if (rel)
    return (
      <button onClick={() => onOpen(rel)} className="rounded-md border border-[var(--line)] bg-[var(--panel)] px-1.5 py-0.5 font-mono text-[0.8em] text-[var(--color-accent)] hover:bg-[var(--panel-2)]">
        📄 {rel.split("/").pop()}
      </button>
    );
  return <code className={className}>{children}</code>;
}

/** Re-renders every second while `on`. */
function useTick(on: boolean) {
  const [, set] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => set((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
}

const clock = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, "0")}`;

function ToolTrail({ tools }: { tools: Tool[] }) {
  const [open, setOpen] = useState(false);
  const running = tools.findLast((t) => t.status === "running");
  useTick(!!running?.startedAt);
  // Long steps (image/video generation, big jobs) look frozen without a clock.
  const elapsed = running?.startedAt ? Date.now() - running.startedAt : 0;
  return (
    <div className="mb-2 text-xs">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--panel)] px-2.5 py-1 text-[var(--muted)] hover:text-[var(--text)]">
        {running ? <span className="h-2 w-2 animate-pulse rounded-full bg-sky-500" /> : <span className="text-emerald-600">✓</span>}
        {running ? `Using ${prettyTool(running.name)}…` : `Used ${tools.length} tool${tools.length > 1 ? "s" : ""}`}
        {running && elapsed >= 15_000 && <span className="tabular-nums">{clock(elapsed)}</span>}
        <span className="text-[10px]">{open ? "▲" : "▼"}</span>
      </button>
      {running && elapsed >= 60_000 && (
        <p className="mt-1.5 text-[var(--muted)]">Still working — some steps (like generating images or video) take a few minutes.</p>
      )}
      {open && (
        <ul className="mt-2 space-y-1 border-l-2 border-[var(--line)] pl-3">
          {tools.map((t, i) => (
            <li key={i} className="text-[var(--muted)]">
              <span className={t.status === "failed" ? "text-red-700" : ""}>{t.status === "failed" ? "✗" : t.status === "running" ? "…" : "✓"} {prettyTool(t.name)}</span>
              {t.preview && <span className="ml-2 font-mono text-[11px] opacity-80">{t.preview.slice(0, 140)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type Pending = Attachment & { id?: string; uploading?: boolean };

function AttachmentStrip({ items, onOpen, onRemove }: { items: Pending[]; onOpen: (p: string) => void; onRemove?: (id: string) => void }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {items.map((a) => (
        <div key={a.id ?? a.path} className="group relative">
          <button disabled={a.uploading} onClick={() => a.path && onOpen(a.path)} className="flex items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1.5 pr-3 text-left text-xs hover:bg-[var(--panel-2)]">
            {a.uploading ? (
              <div className="grid h-10 w-10 place-items-center"><Spinner /></div>
            ) : isImage(a.path) ? (
              <img src={fileUrl(a.path)} alt="" className="h-10 w-10 rounded-lg object-cover" />
            ) : (
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-[var(--panel-2)] text-[10px] font-semibold uppercase">{a.name.split(".").pop()?.slice(0, 4)}</div>
            )}
            <span className="max-w-40 truncate">{a.name}</span>
          </button>
          {onRemove && !a.uploading && (
            <button onClick={() => onRemove(a.id ?? a.path)} className="absolute -top-1.5 -right-1.5 hidden h-5 w-5 place-items-center rounded-full bg-[var(--chrome)] text-xs text-white group-hover:grid" title="Remove">×</button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Composer ──────────────────────────────────────────────────────────── */

function ContextChips({ items, onRemove }: { items: Picked[]; onRemove?: (path: string) => void }) {
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {items.map((c) => (
        <span key={c.path} className="flex items-center gap-1 rounded-full border border-[var(--line)] bg-[var(--panel)] px-2.5 py-0.5 text-xs" title={`Context: ${c.path}`}>
          {c.kind === "dir" ? "📁" : "📄"} <span className="max-w-48 truncate">{c.path}</span>
          {onRemove && <button onClick={() => onRemove(c.path)} className="ml-0.5 text-[var(--muted)] hover:text-red-700" title="Remove">×</button>}
        </span>
      ))}
    </div>
  );
}

function Composer({ busy, canUpload, onSend, onStop, onOpenFile }: { busy: boolean; canUpload: boolean; onSend: (t: string, a: Attachment[], c: Picked[]) => void; onStop: () => void; onOpenFile: (p: string) => void }) {
  const [text, setText] = useState("");
  const [context, setContext] = useState<Picked[]>([]);
  const [picking, setPicking] = useState(false);
  const [files, setFiles] = useState<Pending[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [text]);

  const add = async (list: FileList | File[]) => {
    if (!canUpload) return setError("File uploads aren't enabled for your group.");
    setError(null);
    await Promise.all(
      Array.from(list)
        .slice(0, 10)
        .map(async (f) => {
          const id = crypto.randomUUID();
          setFiles((fs) => [...fs, { id, path: "", name: f.name, uploading: true }]);
          try {
            const up = await uploadFile(f);
            setFiles((fs) => fs.map((x) => (x.id === id ? { id, ...up } : x)));
          } catch (e) {
            setFiles((fs) => fs.filter((x) => x.id !== id));
            setError(`${f.name}: ${(e as Error).message}`);
          }
        }),
    );
  };
  const ready = files.filter((f) => !f.uploading && f.path);
  const canSend = !busy && (text.trim() || ready.length) && !files.some((f) => f.uploading);
  const submit = () => {
    if (!canSend) return;
    onSend(text.trim(), ready.map(({ path, name }) => ({ path, name })), context);
    setText("");
    setFiles([]);
    setContext([]);
  };

  return (
    <div
      className="relative shrink-0 px-4 pb-4"
      onDragOver={(e) => {
        if (!canUpload) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files.length) void add(e.dataTransfer.files);
      }}
    >
      <div className="mx-auto max-w-3xl">
        {error && <p className="mb-2 rounded-lg bg-red-500/10 px-3 py-1.5 text-xs text-red-700">{error}</p>}
        <div className={cx("rounded-2xl border bg-[var(--panel)] shadow-sm transition", dragging ? "border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]/20" : "border-[var(--line)]")}>
          {!!context.length && (
            <div className="px-3 pt-3 [&>div]:justify-start">
              <ContextChips items={context} onRemove={(p) => setContext((c) => c.filter((x) => x.path !== p))} />
            </div>
          )}
          {!!files.length && (
            <div className="px-3 pt-3">
              <AttachmentStrip items={files} onOpen={onOpenFile} onRemove={(id) => setFiles((fs) => fs.filter((f) => (f.id ?? f.path) !== id))} />
            </div>
          )}
          <textarea
            ref={area}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            onPaste={(e) => {
              const imgs = Array.from(e.clipboardData.files);
              if (imgs.length) {
                e.preventDefault();
                void add(imgs);
              }
            }}
            placeholder={dragging ? "Drop files to attach" : "Message your assistant"}
            className="block w-full resize-none bg-transparent px-4 pt-3.5 pb-2 text-[0.95rem] outline-none placeholder:text-[var(--muted)]"
          />
          <div className="flex items-center justify-between px-2.5 pb-2.5">
            <div className="flex items-center">
              <button onClick={() => setPicking(true)} className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-[var(--muted)] hover:bg-[var(--panel-2)] hover:text-[var(--text)]" title="Point your assistant at folders or files in your workspace">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
                <span className="hidden text-xs sm:inline">Context</span>
              </button>
              {canUpload && (
                <>
                  <button onClick={() => input.current?.click()} className="grid h-9 w-9 place-items-center rounded-lg text-[var(--muted)] hover:bg-[var(--panel-2)] hover:text-[var(--text)]" title="Attach files">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21.4 11.6l-9.2 9.2a6 6 0 01-8.5-8.5l9.2-9.2a4 4 0 015.7 5.7l-9.2 9.2a2 2 0 01-2.8-2.8l8.5-8.5" /></svg>
                  </button>
                  <input ref={input} type="file" multiple hidden onChange={(e) => e.target.files && void add(e.target.files).then(() => (e.target.value = ""))} />
                </>
              )}
            </div>
            {busy ? (
              <button onClick={onStop} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--chrome)] text-white hover:bg-[var(--chrome-2)]" title="Stop">
                <span className="h-3 w-3 rounded-sm bg-white" />
              </button>
            ) : (
              <button onClick={submit} disabled={!canSend} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--chrome)] text-white transition hover:bg-[var(--chrome-2)] disabled:bg-[var(--line)]" title="Send">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
              </button>
            )}
          </div>
        </div>
        <p className="mt-2 text-center text-[11px] text-[var(--muted)]">Your assistant can make mistakes. Check important information.</p>
        {picking && (
          <WorkspacePicker
            mode="context"
            title="Use workspace folders or files as context"
            confirmLabel="Add to message"
            onClose={() => setPicking(false)}
            onPick={(p) => {
              setContext((c) => [...c, ...p.filter((x) => !c.some((y) => y.path === x.path))].slice(0, 20));
              setPicking(false);
              area.current?.focus();
            }}
          />
        )}
      </div>
    </div>
  );
}
